import { Container, Title, Text, Box, List } from '@mantine/core';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';

export default function PrivacyPolicy() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    return (
        <PrivacyPolicyContent />
    );
}

export const PrivacyPolicyContent = () => {
    return (
        <Container size="lg" py="xl">
            <Helmet>
                <title>{`Privacy Policy - Thrift Store Nepal`}</title>
                <meta
                    name="description"
                    content="Products - Thrift Store Nepal - Search for your best matching thrift item according to different category and criteria. Buy and sell preowned clothes and other accessories on Nepal's first digital thrift shop - Thrift Store Nepal (Thrift Shop Nepal)"
                />
                <meta property="og:title" content="Products - Thrift Store Nepal" />
                <meta
                    property="og:description"
                    content="Products - Thrift Store Nepal - Buy and sell preowned clothes and other accessories on Nepal's first digital thrift shop - THrift Store Nepal (Thrift Shop Nepal)"
                />
                <meta name="og:image" content="/assets/images/og-2.png" />
            </Helmet>
            <Title order={1} align="center" mb="xl">
                Privacy Policy
            </Title>
            <Box mb="xl">
                <Text fw={600}>
                    Effective Date: January 26, 2025
                </Text>
            </Box>

            <Box mb="xl">
                <Text>
                    At Thrift Store Nepal, your privacy is important to us. This Privacy Policy explains how we collect, use,
                    and protect your information when you use our website or app. By accessing our platform, you agree to the
                    terms outlined in this policy.
                </Text>
            </Box>

            <Box mb="xl">
                <Title order={2} mb="md">
                    1. What Information Do We Collect?
                </Title>
                <Text mb="sm">When you use Thrift Store Nepal, we may collect the following types of information:</Text>

                <Title order={3} mb="sm">1.1 Personal Information</Title>
                <List mb="md">
                    <List.Item>Name, email address, address and phone number when you create an account with us.</List.Item>
                </List>

                <Title order={3} mb="sm">1.2 Transaction Information</Title>
                <List mb="md">
                    <List.Item>Details about the items you buy or sell on the platform.</List.Item>
                </List>

                <Title order={3} mb="sm">1.3 Device Information</Title>
                <List mb="md">
                    <List.Item>Device type, operating system, and unique device identifier used to access our platform.</List.Item>
                </List>

                <Title order={3} mb="sm">1.4 Non-Personal Information</Title>
                <List>
                    <List.Item>Browser type, IP address, and website usage data for analytics purposes.</List.Item>
                </List>
            </Box>

            <Box mb="xl">
                <Title order={2} mb="md">
                    2. How Do We Use Your Information?
                </Title>
                <Text mb="sm">We use your information to provide and improve our services, including:</Text>
                <List>
                    <List.Item>Creating and managing your account.</List.Item>
                    <List.Item>Processing product details and facilitating communication between buyers and sellers.</List.Item>
                    <List.Item>Sending updates about your account.</List.Item>
                    <List.Item>Enforcing our policies and complying with legal obligations.</List.Item>
                </List>
            </Box>

            <Box mb="xl">
                <Title order={2} mb="md">
                    3. How Do We Protect Your Information?
                </Title>
                <Text mb="sm">We take the security of your personal information seriously. Measures we use include:</Text>
                <List mb="md">
                    <List.Item>Industry-standard security technologies and procedures.</List.Item>
                    <List.Item>Encryption of sensitive data during transmission.</List.Item>
                    <List.Item>Regular monitoring of our systems for vulnerabilities and breaches.</List.Item>
                </List>
                <Text>
                    While we strive to protect your data, no system is entirely secure, and we cannot guarantee absolute security.
                </Text>
            </Box>

            <Box mb="xl">
                <Title order={2} mb="md">
                    4. Do We Share Your Information with Third Parties?
                </Title>
                <Text mb="sm">
                    We do not sell, rent, or trade your personal information to third parties. However, we may share your
                    information in the following cases:
                </Text>
                <List>
                    <List.Item>Trusted Service Providers: To help us operate our platform or improve our services.</List.Item>
                    <List.Item>Legal Requirements: To comply with laws, regulations, or legal processes.</List.Item>
                    <List.Item>Protection of Rights: To enforce our policies or protect the safety and rights of users.</List.Item>
                </List>
            </Box>

            <Box mb="xl">
                <Title order={2} mb="md">
                    5. How Can You Access or Modify Your Information?
                </Title>
                <Text mb="sm">
                    You can access or update your personal information by logging into your account on our platform.
                </Text>
                <Text>
                    If you require assistance, please contact our support team at support@thriftstorenepal.com.
                </Text>
            </Box>

            <Box mb="xl">
                <Title order={2} mb="md">
                    6. Cookies and Tracking Technologies
                </Title>
                <Text mb="sm">
                    We use cookies and similar technologies to enhance your browsing experience. Cookies help us:
                </Text>
                <List mb="md">
                    <List.Item>Analyze website usage and performance.</List.Item>
                    <List.Item>Remember your preferences.</List.Item>
                </List>
                <Text>
                    You can manage or disable cookies through your browser settings, though some features may not work correctly if cookies are disabled.
                </Text>
            </Box>

            <Box mb="xl">
                <Title order={2} mb="md">
                    7. Your Rights
                </Title>
                <Text mb="sm">You have the right to:</Text>
                <List mb="md">
                    <List.Item>Access the personal information we hold about you.</List.Item>
                    <List.Item>Update or correct your personal data.</List.Item>
                    <List.Item>Request <a target='_blank' rel='noopener noreferrer' href='/users/account-deactivate'>deletion or deactivation</a> of your personal information, subject to legal requirements.</List.Item>
                    <List.Item>Withdraw your consent for data processing at any time.</List.Item>
                </List>
                <Text>
                    To exercise these rights, contact us at support@thriftstorenepal.com.
                </Text>
            </Box>

            <Box mb="xl">
                <Title order={2} mb="md">
                    8. Changes to This Privacy Policy
                </Title>
                <Text>
                    We may update this Privacy Policy occasionally to reflect changes in our practices or legal requirements.
                    If we make significant changes, we will notify you by posting the updated policy on our website or within the app.
                    Please review this policy regularly to stay informed.
                </Text>
            </Box>

            <Box mb="xl">
                <Title order={2} mb="md">
                    9. Contact Us
                </Title>
                <Text mb="xs">
                    If you have any questions, concerns, or feedback regarding this Privacy Policy or how we handle your data,
                    feel free to reach out to us:
                </Text>
                <Text>
                    Email: info@thriftstorenepal.com
                </Text>
            </Box>

            <Text italic mt="xl" mb="xl">
                Thank you for trusting Thrift Store Nepal to facilitate your buying and selling experience. Your privacy is our priority.
            </Text>
            <Box mt="xl" />
        </Container>
    );
}